import { useEffect, useRef } from 'react';

import { useMutation } from '@tanstack/react-query';

import { openChatStream } from '../services/chat';
import { ContentPart, SendMessageArgs } from '../types/chat';
import { eventToContentPart, parseStreamLine } from '../utils/parsing';

export type UseSendMessageResult = {
  mutation: ReturnType<typeof useMutation<void, unknown, SendMessageArgs>>;
  cancel: () => void;
};

type UseSendMessageCallbacks = {
  onPart: (part: ContentPart) => void;
  onComplete?: () => void;
};

export function useSendMessage(callbacks: UseSendMessageCallbacks): UseSendMessageResult {
  const { onPart } = callbacks;
  const abortRef = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    []
  );

  function createController() {
    const controller = new AbortController();
    abortRef.current = controller;
    return controller;
  }

  function isAbortLikeError(error: Error) {
    const message = error.message;
    const name = error.name;
    return (
      name === 'AbortError' || /aborted|AbortError|BodyStreamBuffer was aborted/i.test(message)
    );
  }

  function processLine(line: string): { complete: boolean } {
    const evt = parseStreamLine(line);
    if (!evt) return { complete: false };
    const part = eventToContentPart(evt);
    if (part) onPart(part);
    return { complete: evt.type === 'complete' };
  }

  async function readStream(
    reader: ReadableStreamDefaultReader<Uint8Array>
  ): Promise<{ sawComplete: boolean }> {
    const decoder = new TextDecoder();
    let buffer = '';
    let sawComplete = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        const { complete } = processLine(line);
        if (complete) sawComplete = true;
      }
    }
    return { sawComplete };
  }

  function scheduleCompletionIfNeeded(sawComplete: boolean, controller: AbortController) {
    if (sawComplete && !controller.signal.aborted) {
      // Use macrotask to yield to React commit phase.
      setTimeout(() => callbacks.onComplete?.(), 0);
    }
  }

  function handleStreamError(error: unknown) {
    if (!(error instanceof Error)) return;
    if (isAbortLikeError(error)) return;
    onPart({
      kind: 'text',
      text: 'An error occurred while processing the request. Please try again.'
    });
    console.error(error.message);
  }

  async function streamMessage(params: SendMessageArgs) {
    const controller = createController();
    try {
      const reader = await openChatStream(params, controller.signal);
      if (!reader) return;
      const { sawComplete } = await readStream(reader);
      scheduleCompletionIfNeeded(sawComplete, controller);
    } catch (error) {
      handleStreamError(error);
    }
  }

  const mutation = useMutation<void, unknown, SendMessageArgs>({
    mutationFn: streamMessage,
    onSettled: () => {
      abortRef.current = null;
    }
  });

  const cancel = () => {
    abortRef.current?.abort();
  };

  return { mutation, cancel };
}
